import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useFormatter } from 'use-intl';
import { useTranslations } from '@/i18n/translations';
import { Button, Eyebrow, I, Icon, Label, Pressable, Row, Stack } from '@leanin/ui';
import { Sortable, ToggleGroup, ToggleGroupItem } from '@leanin/ui/client';
import {
  POLL_DURATION_DAYS,
  POLL_MAX_OPTIONS,
  POLL_MIN_OPTIONS,
  POLL_OPTION_MAX_LEN,
  POLL_VISIBILITIES,
  isPollVisibility,
  isValidPollDuration,
  type PollVisibility,
} from '@leanin/shared/polls';

import type { PollDraft } from './poll-draft';
import styles from './PollComposerBlock.module.css';

const DAY_MS = 24 * 60 * 60 * 1000;

const VISIBILITY_ICON: Record<PollVisibility, ReactNode> = { anonymous: I.lock, public: I.users };
const VISIBILITY_LABEL_KEY = {
  anonymous: 'poll.visibilityAnonymous',
  public: 'poll.visibilityPublic',
} as const satisfies Record<PollVisibility, string>;

/** A row's stable identity is kept locally (the wire contract is a bare
 *  `string[]`), so drag-reorder and per-row focus survive edits — array index
 *  can't be a key once rows move. */
type OptionRowState = { id: string; text: string };
// `id: string` narrows SortableItem's `UniqueIdentifier` (string | number); the
// ids we mint are always strings, so handlers stay string-typed.
type OptionItem = { id: string; label: string; text: string };

export function PollComposerBlock({
  draft,
  onChange,
  onRemove,
}: {
  draft: PollDraft;
  onChange: (next: PollDraft) => void;
  onRemove: () => void;
}) {
  const t = useTranslations('Feed');
  const format = useFormatter();
  const closesAfterId = useId();

  const idSeq = useRef(0);
  const mkId = () => `opt-${idSeq.current++}`;
  const [rows, setRows] = useState<OptionRowState[]>(() =>
    draft.options.map((text) => ({ id: mkId(), text })),
  );

  // DOM refs per row so the keyboard flow (Enter adds + focuses the next row,
  // Backspace-on-empty deletes + focuses the previous) can move the caret.
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const focusAfterRender = useRef<string | null>(null);
  useEffect(() => {
    const id = focusAfterRender.current;
    if (id === null) return;
    focusAfterRender.current = null;
    inputs.current.get(id)?.focus();
  }, [rows]);

  const commit = (next: OptionRowState[]) => {
    setRows(next);
    onChange({ ...draft, options: next.map((r) => r.text) });
  };

  const setText = (id: string, text: string) =>
    commit(rows.map((r) => (r.id === id ? { ...r, text } : r)));

  const addOption = () => {
    if (rows.length >= POLL_MAX_OPTIONS) return;
    const row = { id: mkId(), text: '' };
    focusAfterRender.current = row.id;
    commit([...rows, row]);
  };

  const removeOption = (id: string) => {
    if (rows.length <= POLL_MIN_OPTIONS) return;
    const idx = rows.findIndex((r) => r.id === id);
    focusAfterRender.current = rows[Math.max(0, idx - 1)]?.id ?? null;
    commit(rows.filter((r) => r.id !== id));
  };

  const reorder = (items: OptionItem[]) =>
    commit(
      items
        .map((it) => rows.find((r) => r.id === it.id))
        .filter((r): r is OptionRowState => r !== undefined),
    );

  const onKeyDown = (id: string, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      addOption();
    } else if (e.key === 'Backspace' && e.currentTarget.value === '') {
      e.preventDefault();
      removeOption(id);
    }
  };

  const canRemove = rows.length > POLL_MIN_OPTIONS;
  const canAdd = rows.length < POLL_MAX_OPTIONS;

  // Read at the component boundary; each option previews its end date so the
  // author sees when voting closes, not just the run length.
  const now = Date.now();

  const items: OptionItem[] = rows.map((r, i) => ({
    id: r.id,
    label: r.text.trim() === '' ? t('poll.optionPlaceholder', { n: i + 1 }) : r.text,
    text: r.text,
  }));

  return (
    <div className={styles.region}>
      <Stack gap="sm">
        <Row justify="space-between" align="center">
          <Eyebrow tone="accent">{t('poll.builderTitle')}</Eyebrow>
          <Button variant="ghost" size="sm" aria-label={t('poll.remove')} onClick={onRemove}>
            {t('poll.removeShort')}
          </Button>
        </Row>

        <Sortable<OptionItem>
          items={[...items]}
          onReorder={reorder}
          className={styles.list}
          srStrings={{
            onPickUp: (label) => t('poll.dragPickUp', { label }),
            onMove: (label, position, total) => t('poll.dragMove', { label, position, total }),
            onDrop: (label, position) => t('poll.dragDrop', { label, position }),
            onCancel: (label) => t('poll.dragCancel', { label }),
          }}
        >
          {(item, dragHandle) => {
            const n = rows.findIndex((r) => r.id === item.id) + 1;
            const placeholder = t('poll.optionPlaceholder', { n });
            return (
              <div className={styles.row}>
                <span className={styles.mark} aria-hidden="true" />
                <input
                  ref={(el) => {
                    if (el) inputs.current.set(item.id, el);
                    else inputs.current.delete(item.id);
                  }}
                  className={styles.input}
                  value={item.text}
                  maxLength={POLL_OPTION_MAX_LEN}
                  placeholder={placeholder}
                  aria-label={placeholder}
                  onChange={(e) => setText(item.id, e.currentTarget.value)}
                  onKeyDown={(e) => onKeyDown(item.id, e)}
                />
                {canRemove ? (
                  <span className={styles.remove}>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={I.x}
                      aria-label={t('poll.removeOption')}
                      onClick={() => removeOption(item.id)}
                    />
                  </span>
                ) : null}
                <span className={styles.grip}>{dragHandle}</span>
              </div>
            );
          }}
        </Sortable>

        {canAdd ? (
          <Pressable className={styles.addRow} onClick={addOption}>
            <span className={styles.addMark} aria-hidden="true">
              <Icon size={14}>{I.plus}</Icon>
            </span>
            {t('poll.addOption')}
          </Pressable>
        ) : null}

        <Row justify="space-between" align="center" gap="md" wrap>
          <Row as="span" gap="sm" align="center">
            <Label>{t('poll.whoCanSee')}</Label>
            <ToggleGroup
              type="single"
              variant="segmented"
              value={draft.visibility}
              aria-label={t('poll.whoCanSee')}
              onValueChange={(value) => {
                // Radix single mode reports re-clicking the active item as ''
                // (deselect); a poll always has a visibility, so ignore it.
                if (!isPollVisibility(value)) return;
                onChange({ ...draft, options: rows.map((r) => r.text), visibility: value });
              }}
            >
              {POLL_VISIBILITIES.map((v) => (
                <ToggleGroupItem key={v} value={v}>
                  <Row as="span" gap="xs" align="center">
                    <Icon size={12}>{VISIBILITY_ICON[v]}</Icon>
                    {t(VISIBILITY_LABEL_KEY[v])}
                  </Row>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Row>
          <span className={styles.closesAfter}>
            <Label id={closesAfterId}>{t('poll.closesAfter')}</Label>
            <span className={styles.durationPill}>
              <Icon size={12} color="var(--muted-foreground)">
                {I.clock}
              </Icon>
              <select
                className={styles.durationSelect}
                value={String(draft.durationDays)}
                aria-labelledby={closesAfterId}
                onChange={(e) => {
                  const durationDays = Number(e.currentTarget.value);
                  if (!isValidPollDuration(durationDays)) return;
                  onChange({ ...draft, options: rows.map((r) => r.text), durationDays });
                }}
              >
                {POLL_DURATION_DAYS.map((d) => (
                  <option key={d} value={String(d)}>
                    {t('poll.durationEnds', {
                      days: d,
                      date: format.dateTime(new Date(now + d * DAY_MS), {
                        month: 'short',
                        day: 'numeric',
                      }),
                    })}
                  </option>
                ))}
              </select>
              <Icon size={12} color="var(--muted-foreground)">
                {I.arrowDown}
              </Icon>
            </span>
          </span>
        </Row>
      </Stack>
    </div>
  );
}
